import React, { useState, useEffect } from 'react'
import { Fetch } from '@/utils/Fetch'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import { Controller } from 'react-hook-form'
import Select from 'react-select'

import { toCamelCaseObjectFromSnakeCase } from '@/utils/toCamelCaseObjectFromSnakeCase'

import { DocumentsTabDisplay } from '@/components/features/Document/components'
import { useDraftDocumentReplacer } from '@/components/features/ContractSetting/hooks/useDraftDocumentReplacer'

import { FilesUploadPropsType } from '../../types'
import { DocumentType } from '@/components/features/Document/types'

type Options = {
  value: string
  label: string
}[]

type Users = {
  id: string
  lastName: string
  firstName: string
}[]

type Folders = {
  id: string
  name: string
}[]

export const FilesUpload: React.FC<FilesUploadPropsType> = (props) => {
  const [userOptions, setUserOptions] = useState<Options>([])
  const [folderOptions, setFolderOptions] = useState<Options>([])
  const [documentToDelete, setDocumentToDelete] = useState<DocumentType | null>(null)
  const locationCountOfDocumentToDelete = (props.contractSetting?.questionItems || [])
    .flatMap((item) => item.outputLocations || [])
    .filter((location) => location.documentId === documentToDelete?.id).length
  const { requestReplaceDocument, replacerElement } = useDraftDocumentReplacer(
    props.contractSetting,
    props.isContractSettingFormDirty,
    props.refreshContractSetting,
  )

  const deleteDraftDocument = (documentId: string) => {
    if (!props.refreshContractSetting) return
    const refreshContractSetting = props.refreshContractSetting
    ;(async () => {
      const res = await Fetch(`/api/v1/contract_settings/${props.contractSetting?.id || ''}/draft_documents/${documentId}`, 'DELETE', {})
      if (res.ok) {
        refreshContractSetting()
      }
    })()
  }

  const getUserOptions = async() => {
    const res = await Fetch('/api/v1/users', 'GET', {})
    const json = await res.json()
    const users = toCamelCaseObjectFromSnakeCase<{users: Users}>(json).users
    setUserOptions(users.map(user => ({value: user.id, label: `${user.lastName}${user.firstName}`})))
  }
  const getFolderOptions = async() => {
    const res = await Fetch('/api/v1/folders', 'GET', {})
    const json = await res.json()
    const folders = toCamelCaseObjectFromSnakeCase<{folders: Folders}>(json).folders
    setFolderOptions(folders.map(folder => ({value: folder.id, label: folder.name})))
  }

  useEffect(() => {
    getUserOptions()
    getFolderOptions()
  }, [])

  return (
    <>
    <header className="row align-items-center justify-content-end sticky-top bg-white z-2500">
      <Col className='text-end'>
        <Button
          variant="primary"
          onClick={props.submitContractSetting}
          disabled={!props.isContractSettingFormDirty || props.isSubmittingContractSetting || (props.contractSetting && props.contractSetting.status !== 'draft')}
        >
          保存{!props.isContractSettingFormDirty && '済み'}{props.isSubmittingContractSetting && '中'}
        </Button>
        {
          !!props.contractSetting?.id &&
          <Button
            variant="danger"
            className="ms-3"
            onClick={props.deleteContractSetting}
          >
            削除
          </Button>
        }
      </Col>
    </header>
    <Row>
      <Col className=''>
        <Form onSubmit={props.submitContractSetting}>
          <Row className='gy-2 mt-3'>
            <Form.Label column lg={2} md={3} htmlFor='contractTemplateName'>
              タイトル
            </Form.Label>
            <Col
              lg={10}
              md={9}
            >
              <Form.Control
                {...props.contractSettingRegister('name', {required: true})}
                id='contractTemplateName'
                type="text"
                required
                placeholder='〇〇契約書'
                disabled={props.contractSetting && props.contractSetting.status !== 'draft'}
              />
            </Col>
            {
              folderOptions.length > 0 &&
              <>
                <Form.Label column lg={2} md={3} htmlFor='folderOption'>
                  カテゴリー
                </Form.Label>
                <Col
                  lg={10}
                  md={9}
                >
                  <Controller
                    control={props.contractSettingControl}
                    name={'folderOption'}
                    render={({ field }) => (
                      <Select
                        className='z-2000'
                        options={folderOptions}
                        isDisabled={props.contractSetting && props.contractSetting.status !== 'draft'}
                        {...field}
                      />
                    )}
                  />
                </Col>
              </>
            }
            <Form.Label column lg={2} md={3} htmlFor='defaultApprovers'>
              承認ユーザー
            </Form.Label>
            <Col
              lg={10}
              md={9}
            >
              <Controller
                control={props.contractSettingControl}
                name={'defaultApprovers'}
                render={({ field }) => (
                  <Select
                    className='z-1500'
                    options={userOptions}
                    isMulti
                    isDisabled={props.contractSetting && props.contractSetting.status !== 'draft'}
                    {...field}
                  />
                )}
              />
            </Col>
            <Form.Label column lg={2} md={3} htmlFor='draftDocuments'>
              契約書PDF
            </Form.Label>
            <Col
              lg={10}
              md={9}
            >
              <Form.Control
                {...props.contractSettingRegister('additionalDraftDocuments')}
                id='draftDocuments'
                type="file"
                accept='application/pdf'
                multiple
                disabled={props.contractSetting && props.contractSetting.status !== 'draft'}
              />
            </Col>
          </Row>
        </Form>
        {replacerElement}
        <DocumentsTabDisplay
          documents={props.contractSetting?.draftDocuments || []}
          requestDeleteDocument={props.contractSetting?.status !== 'draft' ? undefined : setDocumentToDelete}
          requestReplaceDocument={props.contractSetting?.status !== 'draft' ? undefined : requestReplaceDocument}
          tabsClassName='pt-5'
        />
      </Col>
    </Row>
    <Modal show={!!documentToDelete} onHide={() => setDocumentToDelete(null)}>
      <Modal.Header closeButton>書類を削除</Modal.Header>
      <Modal.Body>
        <p>「{documentToDelete?.name}」と、この PDF 上の配置（{locationCountOfDocumentToDelete}件）を削除します。</p>
        <p className='mb-0'>質問項目と記入欄は残ります。PDF を追加し直しても、配置は戻りません。</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={() => setDocumentToDelete(null)}>
          キャンセル
        </Button>
        <Button
          variant='danger'
          onClick={() => {
            if (documentToDelete) deleteDraftDocument(documentToDelete.id)
            setDocumentToDelete(null)
          }}
        >
          削除する
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  )
}